import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import type { Order, OrderItem } from '@/types'

/**
 * The credit-note fields the receipt needs from whatever list row the drawer
 * was opened from.
 *
 * Deliberately structural rather than naming a concrete row type: the reports
 * orders browser passes an `OrdersBrowserRow`, the customer profile passes a
 * `CustomerOrderHistoryItem`, and neither should have to know about the other.
 *
 * Keep every field optional so both assign, and keep `cancelled_by_credit_note`
 * on the interface: an all-optional target is a *weak type*, so TS2559 fires if
 * a source shares no properties at all, and that field is the only one
 * `CustomerOrderHistoryItem` carries. Equally, don't promote it to required —
 * `OrdersBrowserRow` declares it optional and would then stop assigning.
 */
export interface ReceiptCreditNoteInfo {
  cancelled_by_credit_note?: boolean
  credit_note_irn?: string | null
  credit_note_total?: number | null
}

export function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="font-medium text-right">{children}</span>
    </div>
  )
}

/**
 * On-screen mini receipt rendered inside the OrderDrawer. Surfaces the items
 * and the totals tally without needing the operator to print a guest receipt.
 *
 * Source-of-truth for ordering and conditional rendering of totals is
 * `lib/printCustomerReceipt.ts` — we match its row order so the screen and
 * the printed slip agree. The one intentional divergence is voided lines:
 * the printer hides them; this card keeps them visible but struck-through
 * because the audit-style review view benefits from seeing what was
 * rung up and reversed.
 */
export function OrderReceiptCard({
  creditNote,
  fullOrder,
  isLoading,
  isError,
  onRetry,
  formatCurrency,
  emptyFallback,
}: {
  creditNote?: ReceiptCreditNoteInfo | null
  fullOrder: Order | null
  isLoading: boolean
  isError: boolean
  onRetry: () => void
  formatCurrency: (n: number) => string
  /** Rendered in place of nothing when the order carries no line items. The
   *  reports drawer omits it — it has plenty of other content to show — while
   *  the customer-profile drawer passes a line, because there the receipt IS
   *  the panel and an empty one would read as a failure. */
  emptyFallback?: React.ReactNode
}) {
  if (isLoading) {
    return (
      <div className="rounded-md border p-4 space-y-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Receipt
        </p>
        <div className="space-y-2 pt-1">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-4 w-4/6" />
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="rounded-md border p-4 space-y-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Receipt
        </p>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Couldn't load items.</span>
          <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" onClick={onRetry}>
            Retry
          </Button>
        </div>
      </div>
    )
  }

  // No items at all — usually means an open tab that never had a KOT fire.
  // Hide the whole card so we don't render an empty Receipt header, unless the
  // caller supplied something to say instead.
  if (!fullOrder || !fullOrder.items || fullOrder.items.length === 0) {
    return emptyFallback ? <>{emptyFallback}</> : null
  }

  const items = fullOrder.items
  const liveItems = items.filter((i) => i.status !== 'voided')
  const totalQty = liveItems.reduce((s, i) => s + i.quantity, 0)
  const cancelledByCN = !!creditNote?.cancelled_by_credit_note

  const subtotal = fullOrder.subtotal ?? 0
  const taxAmount = fullOrder.tax_amount ?? 0
  const serviceCharge = fullOrder.service_charge_amount ?? 0
  const itemDiscounts = fullOrder.item_discounts_total ?? 0
  const billDiscount = fullOrder.discount_amount ?? 0
  const billDiscountPct = fullOrder.discount_percent
  const deliveryFee = fullOrder.delivery_fee_amount ?? 0
  const total = fullOrder.total_amount ?? 0

  const duration = formatOrderDuration(fullOrder.created_at, fullOrder.completed_at)

  return (
    <div className="rounded-md border p-4 space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Receipt
        </p>
        <p className="text-[11px] text-muted-foreground tabular-nums">
          {fullOrder.order_type}
          {duration ? ` · ${duration}` : ''}
        </p>
      </div>

      <div className={cn('space-y-3', cancelledByCN && 'opacity-60')}>
        <ul className="divide-y divide-border/60">
          {items.map((item) => (
            <ReceiptItemRow key={item.id} item={item} formatCurrency={formatCurrency} />
          ))}
        </ul>

        <div className="border-t pt-2 space-y-1 text-sm">
          <TallyRow label="Total qty" value={totalQty.toLocaleString('en-US')} />
          <TallyRow label="Subtotal" value={formatCurrency(subtotal)} />
          <TallyRow label="Sales tax" value={formatCurrency(taxAmount)} />
          {serviceCharge > 0 && (
            <TallyRow label="Service charge" value={formatCurrency(serviceCharge)} />
          )}
          {itemDiscounts > 0 && (
            <TallyRow
              label="Item discounts"
              value={`−${formatCurrency(itemDiscounts)}`}
              muted
            />
          )}
          {billDiscount > 0 && (
            <TallyRow
              label={
                typeof billDiscountPct === 'number' && billDiscountPct > 0
                  ? `Discount (${billDiscountPct}%)`
                  : 'Discount'
              }
              value={`−${formatCurrency(billDiscount)}`}
              muted
            />
          )}
          {deliveryFee > 0 && (
            <TallyRow label="Delivery fee" value={formatCurrency(deliveryFee)} />
          )}
          <div className="flex items-baseline justify-between gap-3 border-t pt-2 mt-1 text-sm">
            <span className="font-semibold uppercase tracking-wide">Total</span>
            <span
              className={cn(
                'font-semibold tabular-nums',
                cancelledByCN && 'line-through',
              )}
            >
              {formatCurrency(total)}
            </span>
          </div>
          {cancelledByCN && (
            <div className="flex items-baseline justify-between gap-3 text-xs text-red-700 dark:text-red-300">
              <span>Cancelled by {creditNote?.credit_note_irn ? 'CN' : 'refund'}</span>
              <span className="tabular-nums">
                −{formatCurrency(creditNote?.credit_note_total ?? total)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function ReceiptItemRow({
  item,
  formatCurrency,
}: {
  item: OrderItem
  formatCurrency: (n: number) => string
}) {
  const voided = item.status === 'voided'
  const name = item.product?.name ?? 'Item'
  const note = (item.special_instructions || item.notes || '').trim()
  const lineDiscount = item.discount_amount ?? 0
  const linePct = item.discount_percent
  return (
    <li className={cn('py-2 text-sm', voided && 'line-through opacity-60')}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="tabular-nums text-muted-foreground shrink-0">
          {item.quantity} ×
        </span>
        <span className="flex-1 break-words">{name}</span>
        <span className="tabular-nums shrink-0">{formatCurrency(item.total_price)}</span>
      </div>
      {/* Chosen options (F-MOD-N6). Same '+' glyph as the printed bill, and
          above the note for the same reason: the options finish describing
          what was sold, the note annotates it. */}
      {(item.modifiers?.length ?? 0) > 0 && (
        <ul className="pl-7 text-xs text-muted-foreground">
          {item.modifiers!.map((m) => (
            <li key={m.id}>
              + {m.quantity > 1 ? `${m.option_name} x${m.quantity}` : m.option_name}
            </li>
          ))}
        </ul>
      )}
      {note && (
        <p className="pl-7 text-xs italic text-muted-foreground">↳ {note}</p>
      )}
      {lineDiscount > 0 && (
        <p className="pl-7 text-xs text-muted-foreground">
          Discount
          {typeof linePct === 'number' && linePct > 0 ? ` (${linePct}%)` : ''}: −
          {formatCurrency(lineDiscount)}
        </p>
      )}
    </li>
  )
}

function TallyRow({
  label,
  value,
  muted,
}: {
  label: string
  value: string
  muted?: boolean
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className={cn('text-xs', muted ? 'text-muted-foreground' : 'text-foreground')}>
        {label}
      </span>
      <span
        className={cn(
          'tabular-nums text-sm',
          muted ? 'text-muted-foreground' : 'text-foreground',
        )}
      >
        {value}
      </span>
    </div>
  )
}

// Inline duration formatter — no shared util exists for "Xm Ys" style.
// Returns null when the order isn't completed (or the math is degenerate)
// so the header strip falls back to just the order_type chip.
function formatOrderDuration(createdAt?: string, completedAt?: string): string | null {
  if (!createdAt || !completedAt) return null
  const start = new Date(createdAt).getTime()
  const end = new Date(completedAt).getTime()
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null
  const secs = Math.round((end - start) / 1000)
  if (secs < 60) return `${secs}s`
  const mins = Math.floor(secs / 60)
  const rem = secs % 60
  if (mins < 60) return rem === 0 ? `${mins}m` : `${mins}m ${rem}s`
  const hrs = Math.floor(mins / 60)
  const minsRem = mins % 60
  return minsRem === 0 ? `${hrs}h` : `${hrs}h ${minsRem}m`
}
